package org.zq.linkgame.ui.components

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import org.zq.linkgame.ui.model.LocalStrings
import org.zq.linkgame.ui.theme.*

/**
 * Country code data for international phone numbers
 */
data class CountryCode(
    val code: String,
    val country: String,
    val flag: String
)

val countryCodeList = listOf(
    CountryCode("+86", "中国", "CN"),
    CountryCode("+1", "USA/Canada", "US"),
    CountryCode("+44", "UK", "UK"),
    CountryCode("+81", "Japan", "JP"),
    CountryCode("+82", "Korea", "KR"),
    CountryCode("+49", "Germany", "DE"),
    CountryCode("+33", "France", "FR"),
    CountryCode("+39", "Italy", "IT"),
    CountryCode("+34", "Spain", "ES"),
    CountryCode("+61", "Australia", "AU"),
    CountryCode("+7", "Russia", "RU"),
    CountryCode("+55", "Brazil", "BR"),
    CountryCode("+91", "India", "IN"),
    CountryCode("+62", "Indonesia", "ID"),
    CountryCode("+66", "Thailand", "TH"),
    CountryCode("+84", "Vietnam", "VN"),
    CountryCode("+60", "Malaysia", "MY"),
    CountryCode("+65", "Singapore", "SG"),
    CountryCode("+63", "Philippines", "PH"),
    CountryCode("+90", "Turkey", "TR"),
    CountryCode("+20", "Egypt", "EG"),
    CountryCode("+234", "Nigeria", "NG"),
    CountryCode("+27", "South Africa", "ZA"),
    CountryCode("+52", "Mexico", "MX"),
    CountryCode("+54", "Argentina", "AR"),
    CountryCode("+57", "Colombia", "CO"),
    CountryCode("+48", "Poland", "PL"),
    CountryCode("+31", "Netherlands", "NL"),
    CountryCode("+30", "Greece", "GR"),
    CountryCode("+966", "Saudi Arabia", "SA"),
    CountryCode("+971", "UAE", "AE"),
    CountryCode("+852", "Hong Kong", "HK"),
    CountryCode("+853", "Macau", "MO"),
    CountryCode("+886", "Taiwan", "TW"),
)

/**
 * Auth mode - Login or Register
 */
enum class AuthMode {
    LOGIN, REGISTER
}

/**
 * Login/Register Dialog
 */
@Composable
fun AuthDialog(
    onDismiss: () -> Unit,
    onLoginSuccess: (username: String) -> Unit = {},
    onRegisterSuccess: (username: String) -> Unit = {}
) {
    val strings = LocalStrings.current
    var authMode by remember { mutableStateOf(AuthMode.LOGIN) }
    
    // Login fields
    var loginPhone by remember { mutableStateOf("") }
    var loginPassword by remember { mutableStateOf("") }
    var loginCountryCode by remember { mutableStateOf(countryCodeList[0]) }
    
    // Register fields
    var registerUsername by remember { mutableStateOf("") }
    var registerPhone by remember { mutableStateOf("") }
    var registerCode by remember { mutableStateOf("") }
    var registerPassword by remember { mutableStateOf("") }
    var registerConfirmPassword by remember { mutableStateOf("") }
    var registerCountryCode by remember { mutableStateOf(countryCodeList[0]) }
    var agreeTerms by remember { mutableStateOf(false) }
    
    // UI state
    var showCountryCodePicker by remember { mutableStateOf(false) }
    var isLoginMode by remember { mutableStateOf(true) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var codeCooldown by remember { mutableStateOf(0) }
    
    Dialog(onDismissRequest = onDismiss) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(Color.White)
                .padding(20.dp)
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Close button
                Box(
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Box(
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .size(32.dp)
                            .clip(RoundedCornerShape(16.dp))
                            .clickable { onDismiss() }
                            .background(LightGray),
                        contentAlignment = Alignment.Center
                    ) {
                        CloseIcon(size = 16.dp, color = DarkGray)
                    }
                }
                
                Spacer(modifier = Modifier.height(8.dp))
                
                // Tab switcher (Login / Register)
                AuthTabSwitcher(
                    isLoginMode = authMode == AuthMode.LOGIN,
                    onModeChange = { isLogin ->
                        authMode = if (isLogin) AuthMode.LOGIN else AuthMode.REGISTER
                        errorMessage = null
                    },
                    loginText = strings.login,
                    registerText = strings.register
                )
                
                Spacer(modifier = Modifier.height(20.dp))
                
                // Error message
                errorMessage?.let { error ->
                    AppText(
                        text = error,
                        color = Color.Red,
                        fontSize = 12.sp
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                }
                
                if (authMode == AuthMode.LOGIN) {
                    // Login Form
                    LoginForm(
                        phone = loginPhone,
                        onPhoneChange = { loginPhone = it },
                        password = loginPassword,
                        onPasswordChange = { loginPassword = it },
                        countryCode = loginCountryCode,
                        onCountryCodeClick = { showCountryCodePicker = true },
                        strings = strings,
                        onLogin = {
                            if (loginPhone.isBlank()) {
                                errorMessage = strings.pleaseEnterPhone
                            } else if (loginPassword.isBlank()) {
                                errorMessage = strings.pleaseEnterPassword
                            } else {
                                // TODO: Implement actual login
                                onLoginSuccess(loginPhone)
                                onDismiss()
                            }
                        }
                    )
                } else {
                    // Register Form
                    RegisterForm(
                        username = registerUsername,
                        onUsernameChange = { registerUsername = it },
                        phone = registerPhone,
                        onPhoneChange = { registerPhone = it },
                        code = registerCode,
                        onCodeChange = { registerCode = it },
                        password = registerPassword,
                        onPasswordChange = { registerPassword = it },
                        confirmPassword = registerConfirmPassword,
                        onConfirmPasswordChange = { registerConfirmPassword = it },
                        countryCode = registerCountryCode,
                        onCountryCodeClick = { showCountryCodePicker = true },
                        agreeTerms = agreeTerms,
                        onAgreeTermsChange = { agreeTerms = it },
                        codeCooldown = codeCooldown,
                        onGetCode = {
                            if (registerPhone.isNotBlank()) {
                                codeCooldown = 60
                                // TODO: Implement send code
                            }
                        },
                        strings = strings,
                        onRegister = {
                            when {
                                registerUsername.isBlank() -> errorMessage = strings.pleaseEnterUsername
                                registerPhone.isBlank() -> errorMessage = strings.pleaseEnterPhone
                                registerCode.isBlank() -> errorMessage = strings.pleaseEnterCode
                                registerPassword.isBlank() -> errorMessage = strings.pleaseEnterPassword
                                registerPassword != registerConfirmPassword -> errorMessage = strings.passwordMismatch
                                !agreeTerms -> errorMessage = strings.pleaseAgreeTerms
                                else -> {
                                    // TODO: Implement actual register
                                    onRegisterSuccess(registerUsername)
                                    onDismiss()
                                }
                            }
                        },
                        onPrivacyPolicyClick = {
                            // TODO: Show privacy policy
                        },
                        onUserAgreementClick = {
                            // TODO: Show user agreement
                        }
                    )
                }
            }
        }
        
        // Country code picker dialog
        if (showCountryCodePicker) {
            CountryCodePickerDialog(
                onDismiss = { showCountryCodePicker = false },
                onSelect = { code ->
                    if (authMode == AuthMode.LOGIN) {
                        loginCountryCode = code
                    } else {
                        registerCountryCode = code
                    }
                    showCountryCodePicker = false
                },
                title = strings.selectCountryCode
            )
        }
    }
    
    // Countdown timer for verification code
    LaunchedEffect(codeCooldown) {
        if (codeCooldown > 0) {
            kotlinx.coroutines.delay(1000)
            codeCooldown--
        }
    }
}

@Composable
private fun AuthTabSwitcher(
    isLoginMode: Boolean,
    onModeChange: (Boolean) -> Unit,
    loginText: String,
    registerText: String
) {
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(25.dp))
            .background(LightGray)
            .padding(4.dp),
        horizontalArrangement = Arrangement.Center
    ) {
        // Login Tab
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(21.dp))
                .background(if (isLoginMode) DeepBlue else Color.Transparent)
                .clickable { onModeChange(true) }
                .padding(horizontal = 32.dp, vertical = 10.dp),
            contentAlignment = Alignment.Center
        ) {
            AppText(
                text = loginText,
                color = if (isLoginMode) Color.White else DarkGray,
                fontWeight = if (isLoginMode) FontWeight.Bold else FontWeight.Normal,
                fontSize = 14.sp
            )
        }
        
        // Register Tab
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(21.dp))
                .background(if (!isLoginMode) DeepBlue else Color.Transparent)
                .clickable { onModeChange(false) }
                .padding(horizontal = 32.dp, vertical = 10.dp),
            contentAlignment = Alignment.Center
        ) {
            AppText(
                text = registerText,
                color = if (!isLoginMode) Color.White else DarkGray,
                fontWeight = if (!isLoginMode) FontWeight.Bold else FontWeight.Normal,
                fontSize = 14.sp
            )
        }
    }
}

@Composable
private fun LoginForm(
    phone: String,
    onPhoneChange: (String) -> Unit,
    password: String,
    onPasswordChange: (String) -> Unit,
    countryCode: CountryCode,
    onCountryCodeClick: () -> Unit,
    strings: org.zq.linkgame.ui.model.AppStrings,
    onLogin: () -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // Phone input with country code
        PhoneInputField(
            phone = phone,
            onPhoneChange = onPhoneChange,
            countryCode = countryCode,
            onCountryCodeClick = onCountryCodeClick,
            label = strings.phoneNumber
        )
        
        // Password input
        AuthTextField(
            value = password,
            onValueChange = onPasswordChange,
            label = strings.password,
            isPassword = true
        )
        
        Spacer(modifier = Modifier.height(8.dp))
        
        // Login button
        AuthButton(
            text = strings.login,
            onClick = onLogin
        )
    }
}

@Composable
private fun RegisterForm(
    username: String,
    onUsernameChange: (String) -> Unit,
    phone: String,
    onPhoneChange: (String) -> Unit,
    code: String,
    onCodeChange: (String) -> Unit,
    password: String,
    onPasswordChange: (String) -> Unit,
    confirmPassword: String,
    onConfirmPasswordChange: (String) -> Unit,
    countryCode: CountryCode,
    onCountryCodeClick: () -> Unit,
    agreeTerms: Boolean,
    onAgreeTermsChange: (Boolean) -> Unit,
    codeCooldown: Int,
    onGetCode: () -> Unit,
    strings: org.zq.linkgame.ui.model.AppStrings,
    onRegister: () -> Unit,
    onPrivacyPolicyClick: () -> Unit,
    onUserAgreementClick: () -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        // Username input
        AuthTextField(
            value = username,
            onValueChange = onUsernameChange,
            label = strings.username
        )
        
        // Phone input with country code
        PhoneInputField(
            phone = phone,
            onPhoneChange = onPhoneChange,
            countryCode = countryCode,
            onCountryCodeClick = onCountryCodeClick,
            label = strings.phoneNumber
        )
        
        // Verification code input with get code button
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(modifier = Modifier.weight(1f)) {
                AuthTextField(
                    value = code,
                    onValueChange = onCodeChange,
                    label = strings.verificationCode,
                    keyboardType = KeyboardType.Number
                )
            }
            
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(if (codeCooldown > 0) LightGray else DeepBlue)
                    .clickable(enabled = codeCooldown == 0) { onGetCode() }
                    .padding(horizontal = 12.dp, vertical = 14.dp)
            ) {
                AppText(
                    text = if (codeCooldown > 0) "${codeCooldown}s" else strings.getCode,
                    color = if (codeCooldown > 0) DarkGray else Color.White,
                    fontSize = 12.sp
                )
            }
        }
        
        // Password input
        AuthTextField(
            value = password,
            onValueChange = onPasswordChange,
            label = strings.password,
            isPassword = true
        )
        
        // Confirm password input
        AuthTextField(
            value = confirmPassword,
            onValueChange = onConfirmPasswordChange,
            label = strings.confirmPassword,
            isPassword = true
        )
        
        // Agreement checkbox
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Checkbox(
                checked = agreeTerms,
                onCheckedChange = onAgreeTermsChange,
                colors = CheckboxDefaults.colors(
                    checkedColor = DeepBlue,
                    uncheckedColor = DarkGray
                )
            )
            
            Row(
                modifier = Modifier.weight(1f),
                verticalAlignment = Alignment.CenterVertically
            ) {
                AppText(
                    text = strings.agreeTerms,
                    fontSize = 12.sp,
                    color = DarkGray
                )
                AppText(
                    text = " ${strings.userAgreement}",
                    fontSize = 12.sp,
                    color = DeepBlue,
                    textDecoration = TextDecoration.Underline,
                    modifier = Modifier.clickable { onUserAgreementClick() }
                )
                AppText(
                    text = " ${strings.and} ",
                    fontSize = 12.sp,
                    color = DarkGray
                )
                AppText(
                    text = strings.privacyPolicy,
                    fontSize = 12.sp,
                    color = DeepBlue,
                    textDecoration = TextDecoration.Underline,
                    modifier = Modifier.clickable { onPrivacyPolicyClick() }
                )
            }
        }
        
        Spacer(modifier = Modifier.height(8.dp))
        
        // Register button
        AuthButton(
            text = strings.register,
            onClick = onRegister
        )
    }
}

@Composable
private fun PhoneInputField(
    phone: String,
    onPhoneChange: (String) -> Unit,
    countryCode: CountryCode,
    onCountryCodeClick: () -> Unit,
    label: String
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Country code selector
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(8.dp))
                .background(LightGray)
                .clickable { onCountryCodeClick() }
                .padding(horizontal = 12.dp, vertical = 14.dp)
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                // Country code badge
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(4.dp))
                        .background(DeepBlue)
                        .padding(horizontal = 6.dp, vertical = 2.dp)
                ) {
                    AppText(
                        text = countryCode.flag,
                        fontSize = 12.sp,
                        color = Color.White,
                        fontWeight = FontWeight.Bold
                    )
                }
                AppText(
                    text = countryCode.code,
                    fontSize = 14.sp,
                    color = DarkGray
                )
                AppText(text = "▼", fontSize = 10.sp, color = DarkGray)
            }
        }
        
        // Phone number input
        Box(modifier = Modifier.weight(1f)) {
            AuthTextField(
                value = phone,
                onValueChange = onPhoneChange,
                label = label,
                keyboardType = KeyboardType.Phone
            )
        }
    }
}

@Composable
private fun AuthTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    isPassword: Boolean = false,
    keyboardType: KeyboardType = KeyboardType.Text
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        label = { AppText(text = label, fontSize = 12.sp) },
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        visualTransformation = if (isPassword) PasswordVisualTransformation() else androidx.compose.ui.text.input.VisualTransformation.None,
        keyboardOptions = KeyboardOptions(keyboardType = if (isPassword) KeyboardType.Password else keyboardType),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = DeepBlue,
            unfocusedBorderColor = LightGray,
            focusedLabelColor = DeepBlue
        ),
        shape = RoundedCornerShape(8.dp)
    )
}

@Composable
private fun AuthButton(
    text: String,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(DeepBlue)
            .clickable { onClick() }
            .padding(vertical = 14.dp),
        contentAlignment = Alignment.Center
    ) {
        AppText(
            text = text,
            color = Color.White,
            fontWeight = FontWeight.Bold,
            fontSize = 16.sp
        )
    }
}

@Composable
private fun CountryCodePickerDialog(
    onDismiss: () -> Unit,
    onSelect: (CountryCode) -> Unit,
    title: String
) {
    Dialog(onDismissRequest = onDismiss) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .heightIn(max = 400.dp)
                .clip(RoundedCornerShape(16.dp))
                .background(Color.White)
                .padding(16.dp)
        ) {
            Column {
                AppText(
                    text = title,
                    fontSize = 18.sp,
                    fontWeight = FontWeight.Bold,
                    color = DeepBlue
                )
                
                Spacer(modifier = Modifier.height(12.dp))
                
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .verticalScroll(rememberScrollState())
                ) {
                    countryCodeList.forEach { code ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { onSelect(code) }
                                .padding(vertical = 12.dp, horizontal = 8.dp),
                            horizontalArrangement = Arrangement.spacedBy(12.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            // Country code badge
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(4.dp))
                                    .background(DeepBlue)
                                    .padding(horizontal = 8.dp, vertical = 4.dp)
                            ) {
                                AppText(
                                    text = code.flag,
                                    fontSize = 12.sp,
                                    color = Color.White,
                                    fontWeight = FontWeight.Bold
                                )
                            }
                            AppText(
                                text = code.country,
                                fontSize = 14.sp,
                                color = DarkGray,
                                modifier = Modifier.weight(1f)
                            )
                            AppText(
                                text = code.code,
                                fontSize = 14.sp,
                                color = DeepBlue,
                                fontWeight = FontWeight.Medium
                            )
                        }
                        
                        if (code != countryCodeList.last()) {
                            HorizontalDivider(color = LightGray, thickness = 0.5.dp)
                        }
                    }
                }
            }
        }
    }
}
